"use client";

import React from 'react';
import { usePathname } from 'next/navigation';
import { AppSidebar } from './app-sidebar';
import { useOperatingContext } from '@/hooks/useOperatingContext';
import {
  isHiddenSidebarRoute,
  isImmersiveRoute,
  isOperatingContextHiddenRoute,
} from '@/lib/is-immersive-route';

type AppShellProps = {
  children: React.ReactNode;
};

export function AppShell({ children }: AppShellProps) {
  const pathname = usePathname() ?? '';
  const operatingContext = useOperatingContext();
  const hideOperatingContext =
    isImmersiveRoute(pathname) ||
    isHiddenSidebarRoute(pathname) ||
    isOperatingContextHiddenRoute(pathname);

  const strategyLabel =
    operatingContext.kind === 'ready' || operatingContext.kind === 'unavailable'
      ? operatingContext.strategyTitle ?? 'Active enterprise strategy'
      : operatingContext.kind === 'error'
        ? 'Enterprise strategy unavailable'
        : 'Loading enterprise strategy…';

  const coverageLabel =
    operatingContext.kind === 'ready' || operatingContext.kind === 'unavailable'
      ? operatingContext.coverage.label
      : operatingContext.kind === 'error'
        ? 'Operating coverage unavailable'
        : 'Loading operating coverage…';

  const statusReason =
    operatingContext.kind === 'unavailable'
      ? operatingContext.reason
      : operatingContext.kind === 'error'
        ? operatingContext.message
        : null;

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <aside className="flex-shrink-0">
        <AppSidebar />
      </aside>
      <main className="min-w-0 flex-1 flex flex-col bg-background">
        {!hideOperatingContext ? (
          <div data-testid="app-shell-operating-context" className="border-b border-border bg-muted/30 px-6 py-3">
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <span className="font-medium text-foreground">Anchored to:</span>
              <span data-testid="app-shell-operating-context-strategy">{strategyLabel}</span>
              <span className="text-muted-foreground/50">·</span>
              <span className="font-medium text-foreground">Coverage:</span>
              <span data-testid="app-shell-operating-context-coverage">{coverageLabel}</span>
            </div>
            {statusReason ? (
              <div data-testid="app-shell-operating-context-reason" className="mt-1 text-[11px] text-muted-foreground">
                {statusReason}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="flex-1 relative">{children}</div>
      </main>
    </div>
  );
}

